import { queryOptions, useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { QuoteCancellation } from '@/types/quoteCancellation';
import type { QuoteRenewalCoTerm } from '@/types/renewalCoTerm';
import { api } from '@/lib/api';
import { QUOTE_REFRESH_INTERVAL_MS, SENDING_REFRESH_INTERVAL_MS } from '@/hooks/quoteRefreshInterval';
import type {
  DiscountDurationKey,
  ExistingSubscriptionTarget,
  FulfillmentModeKey,
  FulfillmentStatusKey,
  QuoteTermLengthBasis,
  QuoteTermStrategyKey,
} from '@/types/quote';
import type { CancelTimingAdmission, QuoteSubscriptionLeg } from '@/types/quoteLegs';
import type { QuoteStartsOnFields, StartsOnFirstPeriod } from '@/types/scheduledStart';
import type { QuoteTermAlignment } from '@/components/quotes/coTermAlignment';
import type {
  PaymentScheduleUnavailableReason,
  QuotePaymentSchedule,
} from '@/types/paymentSchedule';
import type { QuoteCommercialSummary } from '@/types/quoteCommercial';
import type { CheckoutPricePreview } from '@/types/checkoutPricePreview';
import type { CatalogPricingTier } from '@/hooks/useCatalog';
import type { RuleEvaluation } from '@/lib/ruleEvaluations';

export interface QuoteLineItemDiscountSegment {
  id: string;
  startMonth: number;
  durationMonths: number | null;
  discountPercentage: number;
}

export type QuoteStatus = 'Draft' | 'PendingApproval' | 'Approved' | 'Submitted' | 'Sent' | 'Accepted' | 'Declined' | 'Expired' | 'Revoked' | 'Converted';

/** The approver's send-back on the quote's latest approval; the rep edits and resubmits. */
export interface ApprovalRejection {
  approvalId: string;
  rejectedByName: string;
  rejectedByEmail: string | null;
  rejectedAt: string;
  reason: string | null;
}

export interface QuoteSummary {
  id: string;
  quoteNumber: string;
  title?: string | null;
  accountName: string;
  status: QuoteStatus;
  quoteType: string;
  total: number;
  createdAt: string;
  createdByName: string;
  productFamilies: string[];
  viewCount: number;
  lastViewedAt: string | null;
  blendedDiscountPercentage: number | null;
  oneTimeDiscountPercentage?: number | null;
  hasRamp: boolean;
  freeMonths?: number | null;
  isCustomTrack?: boolean;
  fulfillmentStatus?: FulfillmentStatusKey;
  archivedAt?: string | null;
  steadyRecurringSubtotal?: number | null;
  billingFrequency?: string;
  canEdit?: boolean;
  /** Set while a Draft's latest approval was rejected. Absent on an older API. */
  changesRequested?: ApprovalRejection | null;
  /** The billing-zone day a start-later quote starts (YYYY-MM-DD); null on every other quote. */
  startsOn?: string | null;
  scheduledStartCancelledAt?: string | null;
  /** The quote only ends subscriptions and sells nothing; `cancelledMonthly` is what they bill today. */
  cancelOnly?: boolean;
  cancelledMonthly?: number | null;
  /** What the quote reads as (QUOTE_KIND_LABELS): its type, or the Cross-sell or Cancellation its shape makes it. */
  kind?: string;
  createdByUserId?: string;
  /** Set on Sent quotes only: when the customer can no longer accept it. */
  expiresAt?: string | null;
  lastActivityAt?: string | null;
}

export interface QuoteListResponse {
  items: QuoteSummary[];
  totalCount: number;
  page: number;
  pageSize: number;
  /** Whether the list left out quotes created by non-sales staff. */
  salesOnly?: boolean;
}

export interface QuoteListParams {
  page?: number;
  pageSize?: number;
  status?: string;
  statuses?: string;
  quoteType?: string;
  fulfillmentStatus?: FulfillmentStatusKey;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  accountId?: string;
  archived?: boolean;
  salesOnly?: boolean;
  followSalesOnlySetting?: boolean;
  kinds?: string;
  ownerIds?: string;
  mine?: boolean;
  products?: string;
  viewed?: boolean;
}

export interface QuoteListRepOption {
  id: string;
  name: string;
  quoteCount: number;
}

export interface QuoteListAccountOption {
  id: string;
  name: string;
  quoteCount: number;
}

export interface QuoteListProductOption {
  code: string;
  name: string;
}

export interface QuoteListFilterOptions {
  reps: QuoteListRepOption[];
  accounts: QuoteListAccountOption[];
  products: QuoteListProductOption[];
  currentUserId: string;
}

export const QUOTES_QUERY_KEY = 'quotes';

export interface QuoteLineItemDetail {
  id: string;
  chargebeeItemPriceId: string | null;
  productFamily: string | null;
  itemName: string;
  description: string | null;
  quantity: number;
  /** Effective (override-aware) unit price. Use catalogUnitPrice for a list-price baseline. */
  unitPrice: number;
  catalogUnitPrice?: number;
  unitPriceOverride?: number | null;
  /**
   * What the line lists at before any concession, as the server resolved it (a stairstep line's
   * catalog step, not its first-tier unitPrice times quantity). Null where no list can be stated;
   * absent on an older API.
   */
  listAmount?: number | null;
  /** What the renewed subscription bills today; an override equal to it is the renewal seed. */
  renewalBaselineUnitPrice?: number | null;
  /** What the amended subscription carries today; what a zero delta leaves in place. */
  renewalBaselineQuantity?: number | null;
  isCustomItem?: boolean;
  effectiveDiscountPercentage?: number;
  discountPercentage: number | null;
  discountAmount: number | null;
  discountDuration: DiscountDurationKey;
  /** The line ends the live item-scoped discount on its item when it lands; absent on an older API. */
  removesStandingDiscount?: boolean;
  totalPrice: number;
  /** What acceptance bills for this line today, as billing computed it; absent on an older API. */
  dueNowAmount?: number | null;
  billingCycle: string | null;
  isAddon: boolean;
  isOneTime: boolean;
  isAutoAttached: boolean;
  isCatalogRequired?: boolean;
  catalogRequiredDiscountAllowed?: boolean;
  isWaived: boolean;
  /**
   * Set by server reconciliation: the item price id whose line replaces this one. Present means the
   * line survives only because Chargebee auto-attaches it, waived at 100%. Absent on an older API.
   */
  replacedByItemPriceId?: string | null;
  sortOrder: number;
  pricingModel?: string | null;
  /**
   * The band schedule of a stairstep or tiered line, which the line does not store. A retired plan
   * the account still holds is missing from the sellable catalog, so this is the only place a saved
   * line can read its bands from. Null on every other model; absent on an older API.
   */
  pricingTiers?: CatalogPricingTier[] | null;
  /** Server-resolved "100 → 95" / "25 → 25" on an amendment line. Present means it, not `quantity`, is what to show. */
  quantityDisplay?: string | null;
  changeNote?: string | null;
  landsAt?: string | null;
  discountSegments?: QuoteLineItemDiscountSegment[];
}

export interface QuoteOptionDetail {
  id: string;
  name: string;
  label: string | null;
  sortOrder: number;
  isSelected: boolean;
  lineItems: QuoteLineItemDetail[];
}

/**
 * `ExecutionHalted` (API !677): acceptance was recorded but provisioning stopped on a leg the
 * executor will not retry on its own; the quote stays Sent. Older APIs never send it, and a value
 * this client does not know must render as a neutral state rather than crash — every consumer
 * keeps a default branch.
 */
export type QuoteReconciliationStatus = 'None' | 'PendingWebhook' | 'WebhookConfirmed' | 'Mismatch' | 'ExecutionHalted';

export interface QuoteFamilyBreakdown {
  productFamily: string;
  lineItemCount: number;
  subtotal: number;
  discount: number;
  total: number;
}

export interface QuoteApproval {
  id: string;
  status: string;
  approvalType: string;
  triggerReason: string | null;
  decisionNotes: string | null;
  approverEmail: string | null;
  approverName: string | null;
  approverOptions?: string | null;
  eligibleApproverUserIds?: string[];
  requestedAt: string;
  decidedAt: string | null;
  ruleEvaluations: RuleEvaluation[] | null;
  policyExceptionReason?: string | null;
  requesterNote?: string | null;
  requesterName?: string | null;
  /** What approving this request does. Absent on an older API, which only ever approved a send. */
  requestedAction?: QuoteRequestedAction;
}

/** The action an approval request unlocks: sending to the customer, or accepting on their behalf. */
export type QuoteRequestedAction = 'Send' | 'AcceptOnBehalf';

/**
 * The server's own reading of a renewal target's recurring bill (API !704): what it bills today,
 * what the renewal quotes, and which way that moves, judged against the same 1% + $0.01 ceiling as
 * the Line items price gate. Advisory; re-derived on every read.
 */
export interface RenewalRecurringChange {
  subscriptionId: string;
  label: string;
  liveMonthly: number;
  quotedMonthly: number;
  liveDiscountPercentage: number | null;
  direction: 'Increase' | 'Unchanged' | 'Decrease';
}

export interface QuoteHubInvite {
  role: string;
  email: string;
  status: string;
  errorCode: string | null;
  error: string | null;
  lastAttemptAt: string | null;
}

/** What Meridian last wrote to the linked HubSpot deal's amount for this quote. */
export interface QuoteHubSpotDealAmount {
  stamped: number | null;
  basis: 'renewal_uplift' | 'amendment_delta' | 'recurring_total';
  quotedMonthly: number | null;
  baselineMonthly: number | null;
  stampedAt: string | null;
  /** The deal's amount before Meridian's first update. */
  beforeStamp: number | null;
  /** HubSpot's amount from the cached deal, once refreshed after the stamp. */
  liveAmount: number | null;
  /** What Meridian would stamp now; set only while this quote has not stamped the deal. Absent on an older API. */
  expected?: number | null;
}

export interface AcceptOnBehalfEligibility {
  eligible: boolean;
  /** Nothing renews, so no card on file is needed. */
  cancelOnly: boolean;
}

export interface QuoteDetail extends QuoteStartsOnFields {
  id: string;
  /** Upsell or Downgrade when the amendment only grows or only shrinks what the partner pays; absent otherwise. */
  amendmentKind?: string | null;
  quoteNumber: string;
  title?: string | null;
  accountId: string | null;
  accountName: string;
  hubspotCompanyId: string | null;
  hubspotCompanyName: string | null;
  status: QuoteStatus;
  quoteType: string;
  billingFrequency: string;
  contractTermYears: number;
  contractTermMonths: number | null;
  /** Days a proof of concept runs for. Null on every other quote type. */
  pocTermDays?: number | null;
  /** Stamped once the dated end is scheduled in Chargebee; null until then. */
  pocEndsAt?: string | null;
  termPromiseApplicable?: boolean;
  paymentTiming: string | null;
  subtotal: number;
  tax: number;
  total: number;
  currencyCode: string;
  contactFirstName: string | null;
  contactLastName: string | null;
  contactEmail: string | null;
  notes: string | null;
  customTerms: boolean;
  customTermsNotes: string | null;
  /** Rep opted in to sending automatically once the pending approval lands. */
  sendWhenApproved: boolean;
  /** Only the owner or the pending approval's requester may change sendWhenApproved. */
  canSetSendWhenApproved: boolean;
  /** A send holds the quote's send claim right now (another tab, the auto-send job). Absent on an older API. */
  isSending?: boolean;
  /** The pending approval accepts the quote for the customer once it lands. Absent on an older API. */
  acceptOnBehalfWhenApproved?: boolean;
  coTermDate: string | null;
  coTermSubscriptionId: string | null;
  termStrategy?: QuoteTermStrategyKey;
  /** Absent on an older API, which only ever extended by the quoted months. */
  termLengthBasis?: QuoteTermLengthBasis;
  /** TotalFromExpiry only: the anchor billing cycles the saved end adds. Absent on an older API. */
  extensionCycles?: number | null;
  amendmentSubscriptionId?: string | null;
  /** Account subscriptions included in a shared-billing-date quote. Absent on an older API. */
  alignedSubscriptionIds?: string[] | null;
  /** StartNewTerm only: active subscriptions deliberately left on their current term. */
  keptOnCurrentTermSubscriptionIds?: string[] | null;
  /** The kept set resolved to product name and current contract end. Absent on an older API. */
  keptOnCurrentTerm?: { subscriptionId: string; name: string; contractEnd: string | null }[] | null;
  /** Absent on an older API; synthesised from the scalar for a quote that declared none. */
  existingSubscriptionTargets?: ExistingSubscriptionTarget[] | null;
  termAnchorSubscriptionId?: string | null;
  /** Absent on an older API. When this quote's contraction lands; an absent value is `TermEnd`. */
  contractionTiming?: string | null;
  amendmentQuantityBasis?: string | null;
  hubspotDealId: string | null;
  hubspotDealName?: string | null;
  expiresAt: string;
  /** The rep's picked expiry day (YYYY-MM-DD); null when never picked. Absent on an older API. */
  expiryDate?: string | null;
  /** The last day the quote can be accepted (YYYY-MM-DD) in the billing time zone. Absent on an older API. */
  expiresOn?: string;
  /** Short label for the billing time zone the expiry ends in, e.g. "ET". Absent on an older API. */
  expiryTimeZone?: string;
  approvedAt: string | null;
  sentAt: string | null;
  acceptedAt: string | null;
  declineReason: string | null;
  declineNote: string | null;
  createdAt: string;
  createdByName: string;
  /** The quote's current owner. Absent on an older API. */
  createdByUserId?: string | null;
  /** False when the viewer reads this quote only as a member of its account team. */
  canEdit?: boolean;
  /** ScalePad Hub member invites staged by this quote. Absent on an older API. */
  hubInvites?: QuoteHubInvite[] | null;
  /** Null when this quote never stamped its deal. Absent on an older API. */
  hubspotDealAmount?: QuoteHubSpotDealAmount | null;
  /** The server's verdict on accepting for the customer; status and the viewer's access are judged separately. */
  acceptOnBehalf?: AcceptOnBehalfEligibility | null;
  /** Set while a Draft's latest approval was rejected. Absent on an older API. */
  changesRequested?: ApprovalRejection | null;
  /** StartNewTerm only: the day the frozen legs start their new term (YYYY-MM-DD). */
  anchoredOn?: string | null;
  /** StartNewTerm only: the due-at-signing total shown when the quote was sent. */
  sentDueAtSigningTotal?: number | null;
  /** The latest expiry the rep can pick today (YYYY-MM-DD). Absent on an older API. */
  expiryLatestDate?: string | null;
  /** The billing-zone day the quote starts on (YYYY-MM-DD); null starts on acceptance. Absent on an older API. */
  startsOn?: string | null;
  /** The instant the sent legs froze for that day; null before the quote is sent. */
  startsAt?: string | null;
  /** Where an accepted start-later quote stands; null on every other quote. */
  scheduledStart?: QuoteScheduledStart | null;
  options: QuoteOptionDetail[];
  reconciliationStatus: QuoteReconciliationStatus;
  reconciledAt: string | null;
  legs?: QuoteSubscriptionLeg[];
  /** One per Cancel target, present on drafts before any leg exists (API !698); absent on an older API. */
  cancelTimingAdmissions?: CancelTimingAdmission[] | null;
  /** One per renewal target: what it bills today against what the renewal quotes (API !704). Null when nothing renews; absent on an older API. */
  renewalRecurringChanges?: RenewalRecurringChange[] | null;
  /**
   * The account's other active subscriptions this quote co-terms without otherwise changing,
   * plus the ones it explicitly leaves alone. Absent until the quote is sent.
   */
  termAlignments?: QuoteTermAlignment[] | null;
  /**
   * Where a NEW subscription this quote creates actually lands its first term end, per Chargebee's
   * own projection — the nearest achievable cycle when coTermDate sits off its lattice. Null until
   * a submit preflight or send has estimated it.
   */
  newSubscriptionTermEndsAt?: string | null;
  /**
   * What the send did that the rep needs to know but that did not stop it. Only the send response
   * carries these; a later read of the quote has none.
   */
  sendWarnings?: string[] | null;
  familyBreakdown?: QuoteFamilyBreakdown[];
  onboardingContactName: string | null;
  onboardingContactEmail: string | null;
  onboardingContactSyncedToHubSpotAt: string | null;
  viewCount: number;
  firstViewedAt: string | null;
  lastViewedAt: string | null;
  acceptance: QuoteAcceptanceSummary | null;
  approvals: QuoteApproval[];
  paymentSchedule?: QuotePaymentSchedule | null;
  paymentScheduleUnavailableReason?: PaymentScheduleUnavailableReason | null;
  commercialSummary?: QuoteCommercialSummary | null;
  /** Every subscription this quote ends, with what it bills today. Null when it cancels nothing; absent on an older API. */
  cancellations?: QuoteCancellation[] | null;
  /** RenewOnAnchorDate only: the API's plan for every renewal on the anchor's date. Null on every other strategy. */
  coTerm?: QuoteRenewalCoTerm | null;
  renewalEffectiveDate: string | null;
  rampScheduledAt: string | null;
  isCustomTrack?: boolean;
  hubspotPipelineId?: string | null;
  fulfillmentMode?: FulfillmentModeKey;
  fulfillmentStatus?: FulfillmentStatusKey;
  customerAddendum?: string | null;
  archivedAt?: string | null;
  checkoutPricePreview?: CheckoutPricePreview | null;
  updatedAt?: string | null;
  originalQuoteId?: string | null;
  versionNumber?: number;
  steadyRecurringSubtotal?: number | null;
  /** The start-day invoice of a quote with a start date, frozen at send; null before then. Absent on an older API. */
  startsOnSubtotal?: number | null;
  startsOnTax?: number | null;
  startsOnTotal?: number | null;
  startsOnFirstPeriod?: StartsOnFirstPeriod | null;
}

export type ScheduledStartState = 'Scheduled' | 'Started' | 'Paid' | 'Cancelled';

/** Expected* is the start-day invoice frozen at send; Invoiced* is what Chargebee raised on the day. */
export interface QuoteScheduledStart {
  state: ScheduledStartState;
  startsAt: string;
  serviceStartedAt: string | null;
  expectedSubtotal: number | null;
  expectedTax: number | null;
  expectedTotal: number | null;
  invoicedSubtotal: number | null;
  invoicedTax: number | null;
  invoicedTotal: number | null;
  invoicedAt: string | null;
  startInvoiceIds: string[];
  verifiedAt: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
}

export interface QuoteAcceptanceSummary {
  signerName: string;
  signerEmail: string;
  acceptedAt: string;
  ipAddress: string;
  certificateEmailedAt: string | null;
  method: AcceptanceMethod;
  acceptedByName: string | null;
}

export type AcceptanceMethod = 'Clickwrap' | 'RepOnBehalf';

export function useQuoteDetail(id: string | undefined) {
  return useQuery({
    queryKey: [QUOTES_QUERY_KEY, id],
    queryFn: async () => {
      const { data } = await api.get<QuoteDetail>(`/quotes/${id}`);
      return data;
    },
    enabled: !!id,
    // A send in flight lands in seconds; polling at the usual pace would leave Send held for half a minute.
    refetchInterval: (query) => {
      const quote = query.state.data;
      if (quote?.isSending) return SENDING_REFRESH_INTERVAL_MS;
      return quote?.status === 'Draft' ? false : QUOTE_REFRESH_INTERVAL_MS;
    },
  });
}

export interface QuoteSubmitPreview {
  requiresApproval: boolean;
  blocked: boolean;
  reasons: string[];
  measured: boolean;
}

// What submitting the saved draft would do, rehearsed server-side by the same policy pass submit
// runs. Keyed under the quote so the invalidation after every save refetches it; focus refetches
// are off because nothing about the draft changes while the tab is away.
export function useQuoteSubmitPreview(id: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: [QUOTES_QUERY_KEY, id, 'submit-preview'],
    queryFn: async () => {
      const { data } = await api.get<QuoteSubmitPreview>(`/quotes/${id}/submit-preview`);
      return data;
    },
    enabled: enabled && !!id,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useDeleteQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/quotes/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUOTES_QUERY_KEY] });
    },
  });
}

// Everything under the quotes key needs a refresh after a status write except two keys: the share
// link, which the mutation removes outright rather than refetching, and the detail the caller has
// just written from the mutation response.
export function invalidateQuoteQueries(queryClient: QueryClient, freshDetailId: string) {
  queryClient.invalidateQueries({
    predicate: (query) =>
      query.queryKey[0] === QUOTES_QUERY_KEY &&
      query.queryKey[2] !== 'share-link' &&
      !(query.queryKey.length === 2 && query.queryKey[1] === freshDetailId),
  });
}

// Writes a mutation's own response over the detail cache. Any read already in flight for the quote
// was issued before the transition, so it is cancelled first: letting it land would put the
// pre-transition copy back over the one the server just returned.
export async function primeQuoteDetailCache(queryClient: QueryClient, quote: QuoteDetail) {
  const detailKey = [QUOTES_QUERY_KEY, quote.id];
  await queryClient.cancelQueries({ queryKey: detailKey, exact: true });
  queryClient.setQueryData(detailKey, quote);
  invalidateQuoteQueries(queryClient, quote.id);
}

export function useRevokeQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      reason,
      markAsLost,
    }: {
      id: string;
      reason: string;
      markAsLost?: boolean;
    }) => {
      const body = markAsLost ? { reason, markAsLost: true } : { reason };
      const { data } = await api.post<QuoteDetail>(`/quotes/${id}/revoke`, body);
      return data;
    },
    onSuccess: async (quote, variables) => {
      queryClient.setQueryData([QUOTES_QUERY_KEY, variables.id], quote);
      const shareLinkKey = [QUOTES_QUERY_KEY, variables.id, 'share-link'];
      await queryClient.cancelQueries({ queryKey: shareLinkKey, exact: true });
      queryClient.removeQueries({ queryKey: shareLinkKey, exact: true });
      invalidateQuoteQueries(queryClient, variables.id);
    },
  });
}

export function useCreateQuoteVersion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<QuoteDetail>(`/quotes/${id}/versions`);
      return data;
    },
    onSuccess: (quote) => {
      queryClient.setQueryData([QUOTES_QUERY_KEY, quote.id], quote);
      invalidateQuoteQueries(queryClient, quote.id);
    },
    onError: (_error, id) => {
      queryClient.invalidateQueries({ queryKey: [QUOTES_QUERY_KEY, id] });
    },
  });
}

// Submit routes for approval or auto-approves inside the same request, so its response is the only
// thing that knows which of the two happened — a bare invalidate would leave the page rendering
// Draft with a live "Send to customer" button that can only 409.
export interface SubmitQuoteVars {
  id: string;
  sendWhenApproved?: boolean;
  overrideBlocks?: { reason: string };
  note?: string;
  requestedAction?: QuoteRequestedAction;
  attestationNote?: string;
}

export type SubmitQuoteBody = Omit<SubmitQuoteVars, 'id'>;

// Undefined when no option is set: the plain submit has always posted no body at all, and an
// explicit empty object would still set a content type that path has never needed.
export function submitQuoteBody({
  sendWhenApproved,
  overrideBlocks,
  note,
  requestedAction,
  attestationNote,
}: SubmitQuoteBody): SubmitQuoteBody | undefined {
  const body: SubmitQuoteBody = {};
  if (overrideBlocks !== undefined) body.overrideBlocks = overrideBlocks;
  if (sendWhenApproved !== undefined) body.sendWhenApproved = sendWhenApproved;
  const trimmedNote = note?.trim();
  if (trimmedNote) body.note = trimmedNote;
  if (requestedAction !== undefined) body.requestedAction = requestedAction;
  const trimmedAttestation = attestationNote?.trim();
  if (trimmedAttestation) body.attestationNote = trimmedAttestation;
  return Object.keys(body).length > 0 ? body : undefined;
}

export async function postQuoteSubmit(vars: SubmitQuoteVars): Promise<QuoteDetail> {
  const body = submitQuoteBody(vars);
  const { data } = body === undefined
    ? await api.post<QuoteDetail>(`/quotes/${vars.id}/submit`)
    : await api.post<QuoteDetail>(`/quotes/${vars.id}/submit`, body);
  return data;
}

export function useSubmitQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: string | SubmitQuoteVars) =>
      postQuoteSubmit(typeof vars === 'string' ? { id: vars } : vars),
    onSuccess: async (quote) => {
      await primeQuoteDetailCache(queryClient, quote);
      // useApprovals imports this module, so its key is spelled out rather than imported.
      queryClient.invalidateQueries({ queryKey: ['approvals'] });
    },
    onError: (_error, vars) => {
      const id = typeof vars === 'string' ? vars : vars.id;
      queryClient.invalidateQueries({ queryKey: [QUOTES_QUERY_KEY, id] });
    },
  });
}

// Returns an approved-but-unsent or pending-approval quote to Draft so the wizard can edit it. The
// approval it had (or was waiting on) is spent: requesting again re-runs routing on the edited terms.
export function useReopenQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<QuoteDetail>(`/quotes/${id}/reopen`);
      return data;
    },
    onSuccess: (quote) => {
      // The edit route redirects anything that is not a Draft straight back out, so the detail cache
      // has to hold the reopened quote before the navigation that follows — an invalidate alone
      // would let the stale Approved copy answer the edit page's first read.
      queryClient.setQueryData([QUOTES_QUERY_KEY, quote.id], quote);
      queryClient.invalidateQueries({ queryKey: [QUOTES_QUERY_KEY] });
    },
    onError: (_error, id) => {
      // Every rejection means the quote is not what this page thinks it is — someone else is sending
      // it, or it moved on. Refetch so the Edit affordance disappears instead of inviting a retry
      // into the same conflict.
      queryClient.invalidateQueries({ queryKey: [QUOTES_QUERY_KEY, id] });
    },
  });
}

export function useSendQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<QuoteDetail>(`/quotes/${id}/send`);
      return data;
    },
    // sendWarnings describe this one send, not the quote. Caching them would replay the warning on
    // every later read of the detail cache.
    onSuccess: (quote) => primeQuoteDetailCache(queryClient, { ...quote, sendWarnings: null }),
  });
}

export function useResendQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<QuoteDetail>(`/quotes/${id}/resend`);
      return data;
    },
    onSuccess: (quote) => primeQuoteDetailCache(queryClient, quote),
  });
}

export function useExtendQuoteExpiry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, expiryDate }: { id: string; expiryDate: string }) => {
      const { data } = await api.post<QuoteDetail>(`/quotes/${id}/expiry`, { expiryDate });
      return data;
    },
    onSuccess: (quote) => primeQuoteDetailCache(queryClient, quote),
    onError: (_error, { id }) => {
      queryClient.invalidateQueries({ queryKey: [QUOTES_QUERY_KEY, id] });
    },
  });
}

export interface QuoteReconciliationRetryResult {
  quoteId: string;
  quoteNumber: string;
  reconciliationStatus: QuoteReconciliationStatus;
  reconciledAt: string | null;
  changed: boolean;
  detail: string;
}

export function useRetryQuoteReconciliation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<QuoteReconciliationRetryResult>(
        `/quotes/${id}/reconciliation/retry`,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUOTES_QUERY_KEY] });
    },
  });
}

export interface QuoteFulfillmentResult {
  quoteId: string;
  quoteNumber: string;
  fulfillmentStatus: FulfillmentStatusKey;
  completedAt: string;
  actorEmail: string;
  note: string | null;
}

export function useCompleteQuoteFulfillment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, note }: { id: string; note?: string | null }) => {
      const { data } = await api.post<QuoteFulfillmentResult>(
        `/quotes/${id}/fulfillment/complete`,
        { note: note?.trim() ? note.trim() : null },
      );
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUOTES_QUERY_KEY, variables.id] });
      queryClient.invalidateQueries({ queryKey: [QUOTES_QUERY_KEY] });
    },
  });
}

export interface QuoteShareLink {
  url: string;
}

export function useQuoteShareLink(id: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: [QUOTES_QUERY_KEY, id, 'share-link'],
    queryFn: async ({ signal }) => {
      const { data } = await api.get<QuoteShareLink>(`/quotes/${id}/share-link`, { signal });
      return data;
    },
    enabled: !!id && enabled,
    retry: false,
    staleTime: 5 * 60_000,
  });
}

function extractPdfFileName(contentDisposition: string | undefined, fallback: string) {
  if (!contentDisposition) return fallback;
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(contentDisposition);
  if (!match) return fallback;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

export function useDownloadAgreementPdf() {
  return useMutation({
    mutationFn: async ({ id, quoteNumber }: { id: string; quoteNumber: string }) => {
      const response = await api.get(`/quotes/${id}/agreement`, { responseType: 'blob' });
      const fileName = extractPdfFileName(
        response.headers['content-disposition'] as string | undefined,
        `${quoteNumber}-agreement.pdf`,
      );
      const url = URL.createObjectURL(response.data as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    },
  });
}

export function quoteListQueryOptions(params: QuoteListParams = {}) {
  return queryOptions({
    queryKey: [QUOTES_QUERY_KEY, params],
    refetchInterval: QUOTE_REFRESH_INTERVAL_MS,
    queryFn: async () => {
      const { data } = await api.get<QuoteListResponse>('/quotes', { params });
      return data;
    },
  });
}

export function useQuotes(params: QuoteListParams = {}) {
  return useQuery(quoteListQueryOptions(params));
}

export function useQuoteListFilterOptions(params: { salesOnly?: boolean; archived?: boolean }) {
  return useQuery({
    queryKey: [QUOTES_QUERY_KEY, 'filter-options', params],
    queryFn: async () => {
      const { data } = await api.get<QuoteListFilterOptions>('/quotes/filter-options', { params });
      return data;
    },
    staleTime: 60 * 1000,
  });
}

export interface QuoteTimelineEntry {
  id: string;
  sourceId: string;
  type: string;
  action: string;
  description: string;
  timestamp: string;
  metadata: Record<string, unknown>;
}

export interface QuoteTimelineResponse {
  items: QuoteTimelineEntry[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export function useQuoteTimeline(quoteId: string | undefined, page = 1) {
  return useQuery({
    queryKey: [QUOTES_QUERY_KEY, quoteId, 'timeline', page],
    queryFn: async ({ signal }) => {
      const { data } = await api.get<QuoteTimelineResponse>(`/quotes/${quoteId}/timeline`, {
        params: { page, pageSize: 25 }, signal,
      });
      return data;
    },
    enabled: !!quoteId,
    refetchInterval: QUOTE_REFRESH_INTERVAL_MS,
  });
}

